'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { Download, ZoomIn, ZoomOut } from 'lucide-react';

import { renderMermaidSvg } from '../../mermaid/mermaidRender';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const DEFAULT_ZOOM = 1;

const clampZoom = (value: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const downloadSvg = (svg: string): void => {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  try {
    anchor.href = url;
    anchor.download = 'mermaid-diagram.svg';
    document.body.append(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
};

type MermaidPreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; svg: string }
  | { status: 'error'; reason: string };

const useInViewport = (elementRef: RefObject<HTMLElement | null>, rootMargin: string): boolean => {
  const [inViewport, setInViewport] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setInViewport(true);
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, rootMargin]);

  return inViewport;
};

const getPageTheme = (): string => {
  if (typeof document === 'undefined') {
    return 'default';
  }
  return document.documentElement.getAttribute('data-theme') ?? 'default';
};

export const MermaidPreview = ({ source }: { source: string }) => {
  const t = useTranslations('mermaidCodeBlock');
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panOriginRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
  const inViewport = useInViewport(containerRef, '200px');
  const [state, setState] = useState<MermaidPreviewState>({ status: 'idle' });
  const [pageTheme, setPageTheme] = useState<string>(getPageTheme);
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [canPan, setCanPan] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  const zoomIn = useCallback(() => setZoom((prev) => clampZoom(prev + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((prev) => clampZoom(prev - ZOOM_STEP)), []);
  const resetZoom = useCallback(() => setZoom(DEFAULT_ZOOM), []);

  const handlePanStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const element = scrollRef.current;
      if (!element || !canPan || event.button !== 0) {
        return;
      }
      panOriginRef.current = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: element.scrollLeft,
        scrollTop: element.scrollTop,
      };
      element.setPointerCapture(event.pointerId);
      setIsPanning(true);
    },
    [canPan],
  );

  const handlePanMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const element = scrollRef.current;
    const origin = panOriginRef.current;
    if (!element || !origin) {
      return;
    }
    element.scrollLeft = origin.scrollLeft - (event.clientX - origin.x);
    element.scrollTop = origin.scrollTop - (event.clientY - origin.y);
  }, []);

  const handlePanEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const element = scrollRef.current;
    if (element?.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId);
    }
    panOriginRef.current = null;
    setIsPanning(false);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setPageTheme(getPageTheme());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inViewport) {
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      const result = await renderMermaidSvg(source, pageTheme);
      if (cancelled) {
        return;
      }

      if (result.ok) {
        setState({ status: 'ready', svg: result.svg });
        return;
      }

      setState({ status: 'error', reason: result.reason });
    })();

    return () => {
      cancelled = true;
    };
  }, [inViewport, source, pageTheme]);

  const isReady = state.status === 'ready';

  useEffect(() => {
    if (!isReady) {
      setCanPan(false);
      return;
    }
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const update = () => {
      setCanPan(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isReady, state, zoom]);

  return (
    <div ref={containerRef} className="rounded-lg border border-base-300 bg-base-100 p-3">
      {state.status === 'idle' && <div className="text-sm text-[var(--color-fg-muted)]">{t('idle')}</div>}
      {state.status === 'loading' && <div className="text-sm text-[var(--color-fg-muted)]">{t('loading')}</div>}
      {state.status === 'error' && (
        <div className="text-sm text-error">{t('renderFailed', { reason: state.reason })}</div>
      )}
      {isReady && (
        <div className="space-y-2">
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              className="btn btn-xs btn-ghost btn-square"
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              aria-label={t('zoomOut')}
              title={t('zoomOut')}
            >
              <ZoomOut className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn btn-xs btn-ghost min-w-12 font-mono tabular-nums"
              onClick={resetZoom}
              disabled={zoom === DEFAULT_ZOOM}
              aria-label={t('resetZoom')}
              title={t('resetZoom')}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="btn btn-xs btn-ghost btn-square"
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              aria-label={t('zoomIn')}
              title={t('zoomIn')}
            >
              <ZoomIn className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn btn-xs btn-ghost btn-square"
              onClick={() => downloadSvg(state.svg)}
              aria-label={t('downloadSvg')}
              title={t('downloadSvg')}
            >
              <Download className="size-4" aria-hidden="true" />
            </button>
          </div>
          <div
            ref={scrollRef}
            className={`overflow-auto ${canPan ? (isPanning ? 'cursor-grabbing select-none' : 'cursor-grab') : ''}`}
            onPointerDown={handlePanStart}
            onPointerMove={handlePanMove}
            onPointerUp={handlePanEnd}
            onPointerCancel={handlePanEnd}
          >
            <div
              className="[&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
              style={{ width: `${zoom * 100}%` }}
              dangerouslySetInnerHTML={{ __html: state.svg }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const getLanguage = (node: NodeViewProps['node']): string => {
  const attrs = node.attrs as Record<string, unknown>;
  return typeof attrs.language === 'string' ? attrs.language : '';
};

const MermaidCodeBlockNodeView = (props: NodeViewProps) => {
  const t = useTranslations('mermaidCodeBlock');
  const language = useMemo(() => getLanguage(props.node), [props.node]);
  const isMermaid = language === 'mermaid';
  const source = props.node.textContent;
  const [showCode, setShowCode] = useState(false);

  return (
    <NodeViewWrapper as="div" className="my-4">
      {isMermaid && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-medium text-[var(--color-fg-subtle)]">Mermaid</div>
            <button
              type="button"
              className="btn btn-xs btn-ghost"
              onClick={() => setShowCode((prev) => !prev)}
              aria-expanded={showCode}
            >
              {showCode ? t('hideCode') : t('showCode')}
            </button>
          </div>

          <MermaidPreview source={source} />

          <pre className={showCode ? '' : 'hidden'}>
            <code className={language ? `language-${language}` : undefined}>
              <NodeViewContent className="block" />
            </code>
          </pre>
        </div>
      )}

      {!isMermaid && (
        <pre>
          <code className={language ? `language-${language}` : undefined}>
            <NodeViewContent className="block" />
          </code>
        </pre>
      )}
    </NodeViewWrapper>
  );
};

export const MermaidCodeBlock = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(MermaidCodeBlockNodeView);
  },
});
